import React, { useEffect, useState, useCallback } from 'react';
import { Instagram, Link, ExternalLink, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface InstagramIntegrationProps {
  profile: any;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

interface InstagramConnection {
  id: number;
  user_id: string;
  instagram_user_id: string;
  instagram_username: string;
  connected_at: string;
  is_active: boolean;
}

// Custom hook to get window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<{
    width: number | undefined;
    height: number | undefined;
  }>({
    width: undefined,
    height: undefined,
  });
  
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away so state gets updated with initial window size
    handleResize();
    
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  
  return windowSize;
};

export const InstagramIntegration: React.FC<InstagramIntegrationProps> = ({ 
  profile, 
  setError, 
  setSuccess 
}) => {
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<InstagramConnection | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const { width } = useWindowSize();
  
  // Check if device is mobile (less than 640px width)
  const isMobile = width !== undefined && width < 640;

  useEffect(() => {
    fetchInstagramConnection();
  }, [profile]);

  const fetchInstagramConnection = async () => {
    if (!profile?.id) return;
    
    try {
      setLoading(true);
      
      // Check instagram_data in public schema filtered by id_company (single source of truth)
      if (profile?.id_company) {
        const { data: instagramData, error: instagramError } = await supabase
          .from('instagram_data')
          .select('instagram_username')
          .eq('id_company', profile.id_company)
          .maybeSingle();
        
        if (instagramData?.instagram_username) {
          // Create a connection object from the instagram_data
          const connection: InstagramConnection = {
            id: 0,
            user_id: profile.id,
            instagram_user_id: 'unknown',
            instagram_username: instagramData.instagram_username,
            connected_at: new Date().toISOString(),
            is_active: true
          };
          
          setConnection(connection);
          return;
        }
      }
      
      // No connection found
      setConnection(null);
    } catch (err) {
      console.error('Error in fetchInstagramConnection:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectInstagram = () => {
    // Show the instructions modal instead of redirecting directly
    setShowInstructionsModal(true);
  };

  const proceedWithInstagramConnect = () => {
    if (!profile?.id) return;
    
    // Store the user ID in localStorage as a fallback mechanism
    localStorage.setItem('instagram_auth_user_id', profile.id);
    
    // Use the exact URL provided by Meta in their configuration page
    // Just append our state parameter to it
    const instagramAuthUrl = 'https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&client_id=506460105801020&redirect_uri=https://ai-agency-dt7d.onrender.com/webhook/instagram/callback&response_type=code&scope=instagram_business_basic%2Cinstagram_business_manage_messages%2Cinstagram_business_manage_comments%2Cinstagram_business_content_publish%2Cinstagram_business_manage_insights';
    
    // Add the state parameter with the user ID
    const state = encodeURIComponent(profile.id);
    const separator = instagramAuthUrl.includes('?') ? '&' : '?';
    
    // Redirect to Instagram with the state parameter
    window.location.href = `${instagramAuthUrl}${separator}state=${state}`;
  };

  const handleDisconnectInstagram = async () => {
    if (!profile?.id) return;
    
    try {
      setDisconnecting(true);
      setError(null);
      
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/instagram/disconnect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: profile.id
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Cuenta de Instagram desconectada correctamente');
        setConnection(null);
      } else {
        throw new Error(data.error || 'Error al desconectar Instagram');
      }
    } catch (err) {
      console.error('Error disconnecting Instagram:', err);
      setError(err instanceof Error ? err.message : 'Error al desconectar Instagram');
    } finally {
      setDisconnecting(false);
    }
  };

  // Instructions Modal Component
  const InstructionsModal = () => {
    if (!showInstructionsModal) return null;

    return (
      <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Estado de prueba</h3>
          </div>
          <div className="p-6">
            <p className="text-gray-700 mb-4">
              Actualmente la conexión con Instagram está en un estado de prueba, motivo por el cual requerirá de unos pasos extra para realizar la vinculación:
            </p>
            <ul className="list-disc pl-5 mb-4 text-gray-700 space-y-2">
              <li>En una computadora dirígete a <a href="https://www.instagram.com/accounts/manage_access/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">https://www.instagram.com/accounts/manage_access/</a></li>
              <li>Logeate con la cuenta de tu negocio/emprendimiento</li>
              <li>Clickea en la sección de "Invitaciones de prueba"</li>
              <li>Dale a aceptar en la invitación recibida de Atlas-IG</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Esto es necesario para que la conexión entre Atlas y tu página de Instagram se realice de forma segura siguiendo los estándares de Instagram.
            </p>
            <p className="text-gray-700 font-medium">
              Una vez aceptada la invitación puedes continuar con el siguiente paso
            </p>
          </div>
          <div className="p-6 bg-gray-50 flex justify-end gap-2">
            <button
              onClick={() => setShowInstructionsModal(false)}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                setShowContactModal(true);
                setShowInstructionsModal(false);
              }}
              className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-md transition-colors"
            >
              No tengo una invitación para la prueba
            </button>
            <button
              onClick={() => {
                setShowInstructionsModal(false);
                proceedWithInstagramConnect();
              }}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Contact Modal Component
  const ContactModal = () => {
    if (!showContactModal) return null;

    return (
      <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Información de contacto</h3>
          </div>
          <div className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">
                En el caso de no ver ninguna invitación en esa parte contáctate con los desarrolladores para poder tener una invitación válida a esta fase de pruebas
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Contacto:</h4>
              <p className="text-gray-700 mb-1">Instagram: <a href="https://instagram.com/atlas.studio.ia" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">atlas.studio.ia</a></p>
              <p className="text-gray-700">WhatsApp: <a href="https://wa.me/1122635392" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">1122635392</a></p>
            </div>
          </div>
          <div className="p-6 bg-gray-50 flex justify-end">
            <button
              onClick={() => setShowContactModal(false)}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Terms of Service Modal Component
  const TermsModal = () => {
    if (!showTermsModal) return null;

    return (
      <div className="fixed inset-0 bg-brand-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Condiciones de Servicio - Conexión con Instagram</h3>
          </div>
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            <h4 className="text-xl font-semibold text-gray-900 mb-4">Conecta tu cuenta de Instagram con Atlas</h4>
            
            <p className="text-gray-700 mb-4">
              Para poder publicar tus creaciones directamente desde nuestro software, necesitamos que conectes tu cuenta de Instagram.
            </p>
            
            <h5 className="text-lg font-semibold text-gray-800 mt-6 mb-3">¿Para qué usaremos esta conexión?</h5>
            
            <p className="text-gray-700 mb-4">
              El permiso que nos otorgas se usará única y exclusivamente para las siguientes dos acciones, que siempre iniciarás tú:
            </p>
            
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li><span className="font-medium">Publicación Inmediata:</span> Cuando finalices una creación y selecciones la opción "Publicar en Instagram", Atlas la posteará en tu perfil en ese mismo momento.</li>
              <li><span className="font-medium">Publicación Programada:</span> Si estableces una fecha y hora para una publicación, Atlas la posteará automáticamente en tu nombre en el momento exacto que hayas definido.</li>
            </ul>
            
            <h5 className="text-lg font-semibold text-gray-800 mt-6 mb-3">Nuestro Compromiso: Tu Control y Privacidad</h5>
            
            <p className="text-gray-700 mb-4">
              Tu tranquilidad es nuestra máxima prioridad. Por eso, queremos ser absolutamente claros:
            </p>
            
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>NUNCA publicaremos nada en tu perfil sin tu permiso explícito o una programación previa realizada por ti.</li>
              <li>NUNCA accederemos a tus mensajes, ni seguiremos cuentas, ni realizaremos ninguna otra acción en tu nombre.</li>
              <li>La conexión solo se usará para postear el contenido que tú decidas, cuando tú lo decidas.</li>
            </ul>
            
            <p className="text-gray-700 mt-4 font-medium">
              Al hacer clic en "Conectar con Instagram", aceptas estas condiciones y nos das permiso para realizar las publicaciones que tú autorices.
            </p>
          </div>
          <div className="p-6 bg-gray-50 flex justify-end">
            <button
              onClick={() => setShowTermsModal(false)}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Entendido
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm mb-6">
      {/* Render modals */}
      <InstructionsModal />
      <ContactModal />
      <TermsModal />
      
      <div className="p-6 border-b">
        <div className="flex items-center gap-3">
          <Instagram className="w-6 h-6 text-gray-700" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Instagram</h2>
            <p className="text-sm text-gray-500">
              Conecta tu cuenta de Instagram para publicar contenido
            </p>
          </div>
        </div>
      </div>
      
      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : connection ? (
          <div>
            <div className="flex items-center gap-4 p-4 bg-blue-50 border border-blue-100 rounded-md mb-6">
              <CheckCircle2 className="w-6 h-6 text-blue-500 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-blue-700">Cuenta conectada</h3>
                <p className="text-sm text-blue-600">
                  Tu cuenta de Instagram está conectada y lista para usar
                </p>
              </div>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="p-4 bg-gray-50 border border-gray-100 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-md font-medium text-gray-900">
                      @{connection.instagram_username}
                    </h3>
                    <p className="text-sm text-gray-600">
                      Conectada el {new Date(connection.connected_at).toLocaleDateString()}
                    </p>
                  </div>
                  <a 
                    href={`https://instagram.com/${connection.instagram_username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600 flex items-center gap-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">Ver perfil</span>
                  </a>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                onClick={handleDisconnectInstagram}
                disabled={disconnecting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {disconnecting ? 'Desconectando...' : 'Desconectar Instagram'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Instagram className="w-16 h-16 text-blue-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Conecta tu cuenta de Instagram
            </h3>
            
            {isMobile ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-100 rounded-md p-4 text-left">
                  <h4 className="font-medium text-yellow-800 mb-2">Conexión desde computadora</h4>
                  <p className="text-yellow-700 mb-4">
                    La conexión inicial con Instagram debe realizarse desde una computadora.
                  </p>
                  <p className="text-yellow-700 mb-2">
                    Por favor, visita Atlas desde tu computadora para completar la conexión:
                  </p>
                  <a 
                    href="https://app.estudio-atlas.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600 flex items-center gap-2 font-medium"
                  >
                    <Link className="w-4 h-4" />
                    app.estudio-atlas.com
                  </a>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  Conecta tu cuenta de Instagram para publicar contenido directamente desde Atlas
                </p>
                <button
                  onClick={handleConnectInstagram}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Conectar Instagram
                </button>
                
                <div className="text-sm text-gray-600 mt-12 text-center">
                  Al conectar Atlas Studio con Instagram aceptas nuestras <button onClick={() => setShowTermsModal(true)} className="text-blue-500 hover:text-blue-600  focus:outline-none">condiciones de servicio</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
